import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { universityService } from '../services/universityService';
import { editionService } from '../services/editionService';
import { University } from '../types/university.types';
import { Edition } from '../types/edition.types';
import { useAuth } from '../hooks/useAuth';
import { getMediaUrl } from '../config/api.config';
import { Alert, Loader, Confirm, Toast } from '../components/common';

export const UniversityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [university, setUniversity] = useState<University | null>(null);
  const [activeEdition, setActiveEdition] = useState<Edition | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [isVoting, setIsVoting] = useState<boolean>(false);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({show: false, message: '', type: 'success'});
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        
        // Récupérer les détails de l'université
        const universityResponse = await universityService.getActiveVenueUniversities();
        const foundUniversity = universityResponse.data.find(uni => uni.id === parseInt(id));
        
        if (foundUniversity) {
          setUniversity(foundUniversity);
        } else {
          setError('Université non trouvée');
          setIsLoading(false);
          return;
        }
        
        // Récupérer les informations sur l'édition active
        const editionResponse = await editionService.getActiveEditionInfo();
        setActiveEdition(editionResponse.data.edition);
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('Une erreur est survenue lors de la récupération des données');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSupportClick = () => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { redirectTo: `/universites/${id}` } });
    } else {
      setShowConfirm(true);
    }
  };

  const handleConfirmSupport = async () => {
    if (!university || !activeEdition) return;
    
    setShowConfirm(false);
    setIsVoting(true);
    
    try {
      // Appel à l'API pour voter pour cette université
      const payload = {
        university_id: university.id,
        edition_id: activeEdition.id,
        os_device: navigator.platform // Détection automatique de l'OS
      };
      
      const response = await universityService.supportUniversity(payload);
      
      if (response.status === 'success') {
        // Afficher un message de succès
        setToast({
          show: true,
          message: response.message || 'Votre vote a été enregistré avec succès !',
          type: 'success'
        });
        
        // Rafraîchir les données
        const updatedDataResponse = await universityService.getActiveVenueUniversities();
        const updatedUniversity = updatedDataResponse.data.find(uni => uni.id === university.id);
        if (updatedUniversity) {
          setUniversity(updatedUniversity);
        }
      } else {
        // Traiter l'erreur spécifique retournée par l'API
        let errorMessage = response.message;
        
        // Vérifier s'il y a des erreurs spécifiques
        if (response.errors) {
          // Prioriser l'erreur de vote si elle existe
          if (response.errors.vote) {
            errorMessage = response.errors.vote;
          } else {
            // Sinon prendre la première erreur disponible
            const firstError = Object.values(response.errors).find(err => err);
            if (firstError) {
              errorMessage = firstError;
            }
          }
        }
        
        setToast({
          show: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } catch (error) {
      // Gestion des erreurs non-API (réseau, etc.)
      setToast({
        show: true,
        message: error instanceof Error ? error.message : 'Une erreur est survenue lors du vote',
        type: 'error'
      });
    } finally {
      setIsVoting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader size="large" color="primary" text="Chargement des détails..." />
      </div>
    );
  }

  if (error || !university) {
    return (
      <div className="py-8">
        <Alert type="error" message={error || 'Université non trouvée'} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Bannière */}
      <div 
        className="h-40 sm:h-48 md:h-64 bg-cover bg-center rounded-lg shadow-md mb-4 md:mb-6" 
        style={{ backgroundImage: `url(${getMediaUrl(university.banner)})` }}
      />
      
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
        <div className="flex flex-col items-center text-center md:flex-row md:items-start md:text-left md:justify-between mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start">
            <img 
              src={getMediaUrl(university.logo)} 
              alt={`Logo ${university.name}`} 
              className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-primary mb-3 md:mb-0 md:mr-6"
            />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-secondary mb-1 md:mb-2">{university.name}</h1>
              <div className="flex items-center justify-center md:justify-start">
                <span className="text-lg font-bold text-secondary mr-2">{university.supporters_count}</span>
                <span className="text-gray-600">supporters</span>
              </div>
            </div>
          </div>
          
          <div className="w-full md:w-auto mt-4 md:mt-0">
            <button
              onClick={handleSupportClick}
              className={`w-full md:w-auto px-6 py-3 rounded-lg font-medium ${
                university.user_has_voted
                  ? 'bg-green-500 text-white'
                  : 'bg-primary text-tertiary hover:bg-yellow-500'
              } transition-colors`}
              disabled={university.user_has_voted}
            >
              {university.user_has_voted ? 'Déjà supporté' : 'Supporter cette université'}
            </button>
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-4 md:pt-6">
          <h2 className="text-lg md:text-xl font-bold text-secondary mb-3 md:mb-4">À propos de {university.name}</h2>
          <p className="text-sm md:text-base text-gray-600 whitespace-pre-line">{university.description}</p>
        </div>
      </div>
      
      {/* Statistiques ou informations supplémentaires */}
      <div className="grid grid-cols-3 gap-2 md:grid-cols-3 md:gap-6">
        <div className="bg-white rounded-lg shadow-md p-3 md:p-6 text-center">
          <h3 className="text-sm md:text-lg font-bold text-secondary mb-1 md:mb-2">Classement</h3>
          <p className="text-xl md:text-3xl font-bold text-primary">
            {/* Simuler un classement */}
            #{Math.floor(Math.random() * 10) + 1}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-3 md:p-6 text-center">
          <h3 className="text-sm md:text-lg font-bold text-secondary mb-1 md:mb-2">Participation</h3>
          <p className="text-xl md:text-3xl font-bold text-primary">
            {/* Simuler un nombre d'éditions */}
            {Math.floor(Math.random() * 5) + 1} éd.
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-3 md:p-6 text-center">
          <h3 className="text-sm md:text-lg font-bold text-secondary mb-1 md:mb-2">Médailles</h3>
          <p className="text-xl md:text-3xl font-bold text-primary">
            {/* Simuler un nombre de médailles */}
            {Math.floor(Math.random() * 15)}
          </p>
        </div>
      </div>
      
      {/* Boîte de dialogue de confirmation */}
      <Confirm
        isOpen={showConfirm}
        title="Confirmer votre support"
        message={`Êtes-vous sûr de vouloir supporter ${university.name} ? Vous ne pourrez pas changer votre choix pour cette édition des jeux universitaires.`}
        confirmText="Oui, je confirme"
        cancelText="Annuler"
        onConfirm={handleConfirmSupport}
        onCancel={() => setShowConfirm(false)}
        variant="info"
      />
      
      {/* Toast pour les notifications */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({...toast, show: false})}
        />
      )}
      
      {/* Indicateur de chargement pendant le vote */}
      {isVoting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <Loader size="medium" color="primary" text="Enregistrement de votre vote..." />
          </div>
        </div>
      )}
    </div>
  );
};
